import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { STANDARD_DURATIONS } from '@einhaus/project314-shared';
import type { BarDuration } from '@einhaus/project314-types';
import { FaSyncAlt } from 'react-icons/fa';
import UserLayout from '@/components/Layout/UserLayout';
import {
    Alert,
    Button,
    Card,
    DataTable,
    EmptyState,
    FormCheckbox,
    FormNumberInput,
    FormSearchInput,
    FormSelect,
    SearchSelect,
    SegmentedControl,
    StatusBadge,
    type SearchSelectItem
} from '@/components/UI';
import ColumnVisibilityMenu, { type ColumnVisibilityOption } from '@/components/UI/ColumnVisibilityMenu';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import type { ColumnDef, VisibilityState } from '@/components/UI/dataTableConfig';
import type { StatusTone } from '@/components/UI/StatusBadge';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { formatCompactDollars } from '@/utils/formatters';
import { formatPositionSide, formatUnderlyingBias } from '@/utils/tradeSemantics';
import {
    DEFAULT_LEADERBOARD_FILTERS,
    LEADERBOARD_INSTRUMENTS,
    LEADERBOARD_RANK_BY_OPTIONS,
    LEADERBOARD_THRESHOLDS,
    countActiveFilters,
    filterLeaderboardEntries,
    parseLeaderboardFilters,
    serializeLeaderboardFilters,
    instrumentLabel,
    sortByRank,
    withThreshold,
    type LeaderboardEntry,
    type LeaderboardInstrument,
    type LeaderboardFilters,
    type LeaderboardBiasFilter,
    type LeaderboardRoleFilter,
    type LeaderboardStatusFilter,
    type LeaderboardSubscriptionFilter
} from './strategyLeaderboardFilters';

type StrategyMetrics = LeaderboardEntry['metrics'];
type MetricFormat = 'percent' | 'ratio' | 'count' | 'dollars';

const STATUS_OPTIONS: { value: LeaderboardStatusFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'trading', label: 'Trading' },
    { value: 'paused', label: 'Paused' }
];

const ROLE_OPTIONS: { value: LeaderboardRoleFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'base', label: 'Bases' },
    { value: 'variation', label: 'Variations' },
    { value: 'standalone', label: 'Standalone' }
];

const BIAS_OPTIONS: { value: LeaderboardBiasFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'bullish', label: 'Bullish' },
    { value: 'bearish', label: 'Bearish' },
    { value: 'mixed', label: 'Mixed' },
    { value: 'unknown', label: 'Unknown' }
];

const SUBSCRIPTION_OPTIONS: { value: LeaderboardSubscriptionFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'subscribed', label: 'Subscribed' },
    { value: 'switchedOn', label: 'Switched on' },
    { value: 'notSubscribed', label: 'Not subscribed' }
];

const BIAS_TONES: Record<LeaderboardEntry['underlyingBias'], StatusTone> = {
    bullish: 'success',
    bearish: 'danger',
    mixed: 'warning',
    unknown: 'neutral'
};

// Futures legs (and option legs saved without call/put) carry an ownership side but no bullish/bearish exposure under
// the platform's trade semantics, so the long/short side is spelled out instead of guessing a direction.
const biasTitle = (entry: LeaderboardEntry): string =>
    entry.underlyingBias === 'unknown'
        ? `No bullish/bearish exposure can be derived from this strategy's legs (for example futures). Position side: ${formatPositionSide(
              entry.positionSide === 'mixed' || entry.positionSide === 'unknown' ? null : entry.positionSide
          )}.`
        : `${formatUnderlyingBias(entry.underlyingBias)} exposure, from each leg's opening action and call/put type.`;

// The underlying itself (shares, crypto), options and futures read as distinct families at a glance.
const INSTRUMENT_CLASSES: Record<LeaderboardInstrument, string> = {
    stocks: 'bg-sky-950/70 text-sky-300',
    crypto: 'bg-amber-950/70 text-amber-300',
    stockOptions: 'bg-violet-950/70 text-violet-300',
    indexOptions: 'bg-fuchsia-950/70 text-fuchsia-300',
    futures: 'bg-teal-950/70 text-teal-300'
};

const InstrumentsCell = ({ instruments }: { instruments: LeaderboardInstrument[] }) =>
    instruments.length === 0 ? (
        <span className="text-zinc-600" title="This strategy has no legs that can trade">
            —
        </span>
    ) : (
        <div className="flex flex-wrap gap-1">
            {instruments.map((instrument) => (
                <span
                    className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium ${INSTRUMENT_CLASSES[instrument]}`}
                    key={instrument}
                >
                    {instrumentLabel(instrument)}
                </span>
            ))}
        </div>
    );

/** Multi-select toggle chips: a strategy matches when it trades any selected instrument. */
const InstrumentFilter = ({
    selected,
    onChange
}: {
    selected: LeaderboardInstrument[];
    onChange: (instruments: LeaderboardInstrument[]) => void;
}) => (
    <div aria-label="Instruments" className="inline-flex flex-wrap gap-1 rounded-md border border-zinc-700 bg-black/30 p-0.5" role="group">
        {LEADERBOARD_INSTRUMENTS.map(({ value, label }) => {
            const isSelected = selected.includes(value);

            return (
                <button
                    aria-pressed={isSelected}
                    className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
                        isSelected ? 'bg-cyan-600 text-white' : 'text-zinc-400 hover:text-zinc-200'
                    }`}
                    key={value}
                    type="button"
                    onClick={() =>
                        onChange(
                            LEADERBOARD_INSTRUMENTS.map((instrument) => instrument.value).filter((instrument) =>
                                instrument === value ? !isSelected : selected.includes(instrument)
                            )
                        )
                    }
                >
                    {label}
                </button>
            );
        })}
    </div>
);

const DirectionCell = ({ entry }: { entry: LeaderboardEntry }) => (
    <span title={biasTitle(entry)}>
        <StatusBadge status={entry.underlyingBias} tone={BIAS_TONES[entry.underlyingBias]}>
            {formatUnderlyingBias(entry.underlyingBias)}
        </StatusBadge>
    </span>
);

const VISIBLE_PORTFOLIO_COUNT = 3;

const PortfoliosCell = ({ subscriptions }: { subscriptions: LeaderboardEntry['portfolioSubscriptions'] }) => {
    if (subscriptions.length === 0) {
        return <span className="text-zinc-600">—</span>;
    }

    const hidden = subscriptions.slice(VISIBLE_PORTFOLIO_COUNT);

    return (
        <div className="flex max-w-[260px] flex-col gap-0.5 text-[11px]">
            {subscriptions.slice(0, VISIBLE_PORTFOLIO_COUNT).map((subscription) => (
                <Link
                    className={`flex min-w-0 items-center gap-1 hover:underline ${
                        subscription.subscriptionActive ? 'text-zinc-200 hover:text-white' : 'text-zinc-500 hover:text-zinc-300'
                    }`}
                    key={subscription.portfolioId}
                    title={`${subscription.portfolioName} · ${subscription.portfolioType === 'live' ? 'live' : 'paper'} portfolio · subscription ${
                        subscription.subscriptionActive ? 'on' : 'off'
                    }`}
                    to={`/user/portfolioSettings/${subscription.portfolioId}`}
                >
                    {subscription.portfolioType === 'live' && (
                        <span className="shrink-0 rounded bg-green-950 px-1 text-[10px] font-medium text-green-300">Live</span>
                    )}
                    <span className="truncate">{subscription.portfolioName}</span>
                    {!subscription.subscriptionActive && <span className="shrink-0 text-zinc-600">(off)</span>}
                </Link>
            ))}
            {hidden.length > 0 && (
                <span className="text-zinc-500" title={hidden.map((subscription) => subscription.portfolioName).join('\n')}>
                    +{hidden.length} more
                </span>
            )}
        </div>
    );
};

const formatMetric = (format: MetricFormat, value: number): string => {
    switch (format) {
        case 'percent':
            return `${value.toFixed(1)}%`;
        case 'ratio':
            return value.toFixed(2);
        case 'count':
            return value.toLocaleString('en-US');
        case 'dollars':
            return formatCompactDollars(value);
    }
};

const toneClass = (value: number): string => {
    if (value > 0) return 'text-green-300';
    if (value < 0) return 'text-red-300';

    return 'text-zinc-300';
};

// A strategy without a chosen configuration has empty placeholder metrics; show those as missing, not as zeros.
const overall =
    (select: (metrics: StrategyMetrics) => number | null) =>
    (entry: LeaderboardEntry): number | null =>
        entry.bestCombination ? select(entry.metrics) : null;

const walkForward =
    (select: (metrics: StrategyMetrics) => number | null) =>
    (entry: LeaderboardEntry): number | null =>
        entry.bestCombination && entry.walkForward.metrics ? select(entry.walkForward.metrics) : null;

interface MetricColumnConfig {
    id: string;
    label: string;
    title: string;
    format: MetricFormat;
    toned?: boolean;
    hiddenByDefault?: boolean;
    select: (entry: LeaderboardEntry) => number | null;
}

const METRIC_COLUMNS: MetricColumnConfig[] = [
    {
        id: 'trades',
        label: 'Trades',
        title: 'Entered trades in the best configuration',
        format: 'count',
        select: overall((metrics) => metrics.totalTrades)
    },
    {
        id: 'winRate',
        label: 'Win rate',
        title: 'Share of entered trades that made money',
        format: 'percent',
        select: overall((metrics) => metrics.avgWinRate)
    },
    {
        id: 'avgTrade',
        label: 'Avg trade',
        title: 'Average % profit per entered trade',
        format: 'percent',
        toned: true,
        select: overall((metrics) => metrics.avgReturnPercent)
    },
    {
        id: 'roc',
        label: 'Return on risk',
        title: 'Total profit as a % of the capital put at risk',
        format: 'percent',
        toned: true,
        select: overall((metrics) => metrics.avgReturnOnRiskCapitalPercent)
    },
    {
        id: 'netProfit',
        label: 'Net P&L',
        title: 'Total dollar profit across entered trades',
        format: 'dollars',
        toned: true,
        select: overall((metrics) => metrics.netProfit)
    },
    {
        id: 'profitFactor',
        label: 'Profit factor',
        title: 'Gross profit divided by gross loss',
        format: 'ratio',
        select: overall((metrics) => metrics.profitFactor)
    },
    {
        id: 'sharpe',
        label: 'Sharpe',
        title: 'Average Sharpe ratio across backtests',
        format: 'ratio',
        hiddenByDefault: true,
        select: overall((metrics) => metrics.avgSharpeRatio)
    },
    {
        id: 'drawdown',
        label: 'Max drawdown',
        title: 'Average maximum drawdown across backtests',
        format: 'percent',
        select: overall((metrics) => metrics.avgMaxDrawdownPercent)
    },
    {
        id: 'wfTrades',
        label: 'WF trades',
        title: 'Walk-forward: entered trades after the training cutoff',
        format: 'count',
        select: walkForward((metrics) => metrics.totalTrades)
    },
    {
        id: 'wfWinRate',
        label: 'WF win rate',
        title: 'Walk-forward: win rate after the training cutoff',
        format: 'percent',
        select: walkForward((metrics) => metrics.avgWinRate)
    },
    {
        id: 'wfAvgTrade',
        label: 'WF avg trade',
        title: 'Walk-forward: average % profit per trade after the training cutoff',
        format: 'percent',
        toned: true,
        select: walkForward((metrics) => metrics.avgReturnPercent)
    },
    {
        id: 'wfRoc',
        label: 'WF return on risk',
        title: 'Walk-forward: return on risk capital after the training cutoff',
        format: 'percent',
        toned: true,
        hiddenByDefault: true,
        select: walkForward((metrics) => metrics.avgReturnOnRiskCapitalPercent)
    }
];

const COLUMN_OPTIONS: ColumnVisibilityOption[] = [
    { id: 'family', label: 'Family' },
    { id: 'instruments', label: 'Instruments' },
    { id: 'direction', label: 'Direction' },
    { id: 'portfolios', label: 'Portfolios' },
    { id: 'timeframe', label: 'Timeframe' },
    ...METRIC_COLUMNS.map((column) => ({ id: column.id, label: column.label })),
    { id: 'entities', label: 'Entities' },
    { id: 'bestConfiguration', label: 'Best configuration' }
];

const DEFAULT_COLUMN_VISIBILITY: VisibilityState = Object.fromEntries(
    METRIC_COLUMNS.filter((column) => column.hiddenByDefault).map((column) => [column.id, false])
);

const COLUMN_VISIBILITY_STORAGE_KEY = 'strategyLeaderboard.columnVisibility';

// Column choices are a per-browser convenience: storage can be unavailable (private windows) or hold an older
// shape, and either way the default columns are the correct fallback.
const loadColumnVisibility = (): VisibilityState => {
    try {
        const raw = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
        if (raw === null) return DEFAULT_COLUMN_VISIBILITY;

        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return DEFAULT_COLUMN_VISIBILITY;

        return Object.fromEntries(Object.entries(parsed).filter((pair): pair is [string, boolean] => typeof pair[1] === 'boolean'));
    } catch {
        return DEFAULT_COLUMN_VISIBILITY;
    }
};

const saveColumnVisibility = (visibility: VisibilityState): void => {
    try {
        localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
    } catch {
        // Storage is unavailable; the choice still applies for this visit.
    }
};

const MetricValue = ({ format, toned, value }: { format: MetricFormat; toned?: boolean; value: number | null }) => {
    if (value === null) {
        return <span className="text-zinc-600">—</span>;
    }

    return <span className={toned ? toneClass(value) : 'text-zinc-100'}>{formatMetric(format, value)}</span>;
};

const resultStatus = (entry: LeaderboardEntry): { label: string; tone: StatusTone; title: string } => {
    if (entry.versionError) {
        return {
            label: "Can't evaluate",
            tone: 'danger',
            title: `This strategy's current version could not be resolved, so its results can't be read: ${entry.versionError}`
        };
    }

    if (entry.requiresBacktestRefresh) {
        return {
            label: 'Needs re-run',
            tone: 'warning',
            title: 'Results exist only for an older version of this strategy. Backtest the current version to rank it.'
        };
    }

    if (entry.summariesPending) {
        return {
            label: 'Summaries building',
            tone: 'info',
            title: 'Backtests for the current version are still being summarized. Results appear once they finish.'
        };
    }

    return { label: 'No backtests', tone: 'neutral', title: 'The current version of this strategy has not been backtested.' };
};

const BestConfigurationCell = ({ entry }: { entry: LeaderboardEntry }) => {
    if (!entry.bestCombination) {
        const status = resultStatus(entry);

        return (
            <Link title={status.title} to={`/user/tradingStrategy/${entry.strategyId}?section=backtests`}>
                <StatusBadge status={status.label} tone={status.tone}>
                    {status.label}
                </StatusBadge>
            </Link>
        );
    }

    return (
        <div className="max-w-[280px] text-[11px]" title={entry.bestCombination.label}>
            <div className="truncate text-emerald-400/90">{entry.bestCombination.label}</div>
            <div className="text-zinc-500">
                Best of {entry.bestCombination.combinationCount} · {entry.bestCombination.backtestCount} backtests
            </div>
        </div>
    );
};

const StrategyCell = ({ entry, baseName }: { entry: LeaderboardEntry; baseName: string | null }) => (
    <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
            <Link
                className="text-left font-medium text-blue-400 hover:text-blue-300 hover:underline"
                to={`/user/tradingStrategy/${entry.strategyId}`}
            >
                {entry.name}
            </Link>
            {entry.familyRole === 'base' && (
                <StatusBadge status="base" tone="accent">
                    Base
                </StatusBadge>
            )}
            {!entry.active && (
                <StatusBadge status="paused" tone="neutral">
                    Paused
                </StatusBadge>
            )}
        </div>
        {entry.familyRole === 'variation' && entry.baseStrategyId !== null && (
            <div className="mt-0.5 truncate text-[11px] text-zinc-500">
                <Link className="hover:text-zinc-300 hover:underline" to={`/user/tradingStrategy/${entry.baseStrategyId}`}>
                    variation of #{entry.baseStrategyId}
                    {baseName ? ` ${baseName}` : ''}
                </Link>
            </div>
        )}
        {entry.variantLabel && entry.variantLabel !== entry.name && (
            <div className="mt-0.5 truncate text-[11px] text-zinc-500" title={entry.variantLabel}>
                {entry.variantLabel}
            </div>
        )}
    </div>
);

const metricColumn = (config: MetricColumnConfig): ColumnDef<LeaderboardEntry> => ({
    id: config.id,
    header: () => <span title={config.title}>{config.label}</span>,
    accessorFn: (entry): number | undefined => config.select(entry) ?? undefined,
    sortFn: 'basic',
    sortUndefined: 'last',
    sortDescFirst: true,
    minSize: 80,
    size: 105,
    maxSize: 150,
    meta: { className: 'text-right tabular-nums', headerClassName: 'text-right' },
    cell: ({ row }) => <MetricValue format={config.format} toned={config.toned} value={config.select(row.original)} />
});

const buildColumns = (nameById: ReadonlyMap<number, string>): ColumnDef<LeaderboardEntry>[] => [
    {
        id: 'id',
        header: 'ID',
        accessorFn: (entry) => entry.strategyId,
        sortFn: 'basic',
        sortDescFirst: true,
        minSize: 60,
        size: 70,
        maxSize: 90,
        meta: { sticky: 'left' },
        cell: ({ row }) => (
            <Link
                className="font-mono text-xs text-blue-400 hover:text-blue-300 hover:underline"
                to={`/user/tradingStrategy/${row.original.strategyId}`}
            >
                #{row.original.strategyId}
            </Link>
        )
    },
    {
        id: 'strategy',
        header: 'Strategy',
        // Lowercased so the plain comparison sorts names alphabetically instead of by embedded numbers.
        accessorFn: (entry) => entry.name.toLowerCase(),
        sortFn: 'basic',
        minSize: 260,
        size: 320,
        maxSize: 420,
        meta: { sticky: 'left' },
        cell: ({ row }) => (
            <StrategyCell
                baseName={row.original.baseStrategyId === null ? null : (nameById.get(row.original.baseStrategyId) ?? null)}
                entry={row.original}
            />
        )
    },
    {
        id: 'family',
        header: 'Family',
        accessorFn: (entry): string | undefined => entry.familyName?.toLowerCase(),
        sortFn: 'basic',
        sortUndefined: 'last',
        minSize: 110,
        size: 160,
        maxSize: 240,
        cell: ({ row }) =>
            row.original.familyId === null ? (
                <span className="text-zinc-600">—</span>
            ) : (
                <Link
                    className="block truncate text-xs text-zinc-300 hover:text-white hover:underline"
                    title={row.original.familyName ?? undefined}
                    to={`/user/strategyFamilies/${row.original.familyId}`}
                >
                    {row.original.familyName}
                </Link>
            )
    },
    {
        id: 'instruments',
        header: 'Instruments',
        accessorFn: (entry) => entry.instruments.map(instrumentLabel).join(', ').toLowerCase(),
        sortFn: 'basic',
        minSize: 110,
        size: 150,
        maxSize: 220,
        cell: ({ row }) => <InstrumentsCell instruments={row.original.instruments} />
    },
    {
        id: 'direction',
        header: 'Direction',
        accessorFn: (entry) => entry.underlyingBias,
        sortFn: 'basic',
        minSize: 90,
        size: 105,
        maxSize: 130,
        cell: ({ row }) => <DirectionCell entry={row.original} />
    },
    {
        id: 'portfolios',
        header: () => <span title="Subscriptions in active portfolios; dimmed ones are switched off">Portfolios</span>,
        // Sorts by how many portfolios trade it (switch on) first, then by how many hold it at all.
        accessorFn: (entry) =>
            entry.portfolioSubscriptions.filter((subscription) => subscription.subscriptionActive).length * 1000 +
            entry.portfolioSubscriptions.length,
        sortFn: 'basic',
        sortDescFirst: true,
        minSize: 140,
        size: 200,
        maxSize: 280,
        cell: ({ row }) => <PortfoliosCell subscriptions={row.original.portfolioSubscriptions} />
    },
    {
        id: 'timeframe',
        header: 'Timeframe',
        accessorFn: (entry): number | undefined => (entry.duration === null ? undefined : STANDARD_DURATIONS.indexOf(entry.duration)),
        sortFn: 'basic',
        sortUndefined: 'last',
        minSize: 80,
        size: 95,
        maxSize: 120,
        cell: ({ row }) =>
            row.original.duration === null ? (
                <span className="text-zinc-600">—</span>
            ) : (
                <span className="font-mono text-xs text-zinc-300">{row.original.duration}</span>
            )
    },
    ...METRIC_COLUMNS.map(metricColumn),
    {
        id: 'entities',
        header: () => (
            <span title="Entities the best configuration was backtested on, out of the best-covered configuration">Entities</span>
        ),
        accessorFn: (entry): number | undefined => entry.bestCombination?.entityCount,
        sortFn: 'basic',
        sortUndefined: 'last',
        sortDescFirst: true,
        minSize: 80,
        size: 100,
        maxSize: 130,
        meta: { className: 'text-right tabular-nums', headerClassName: 'text-right' },
        cell: ({ row }) =>
            row.original.bestCombination ? (
                <span className="text-zinc-100">
                    {row.original.bestCombination.entityCount}
                    <span className="text-zinc-500">/{row.original.bestCombination.strategyEntityCount}</span>
                </span>
            ) : (
                <span className="text-zinc-600">—</span>
            )
    },
    {
        id: 'bestConfiguration',
        header: 'Best configuration',
        accessorFn: (entry): string | undefined => entry.bestCombination?.label.toLowerCase(),
        sortFn: 'basic',
        sortUndefined: 'last',
        minSize: 160,
        size: 260,
        maxSize: 320,
        cell: ({ row }) => <BestConfigurationCell entry={row.original} />
    }
];

interface FiltersCardProps {
    durations: BarDuration[];
    familyItems: SearchSelectItem[];
    filters: LeaderboardFilters;
    onChange: (patch: Partial<LeaderboardFilters>) => void;
    onClear: () => void;
}

const FiltersCard = ({ durations, familyItems, filters, onChange, onClear }: FiltersCardProps) => {
    const activeFilterCount = countActiveFilters(filters);

    return (
        <Card
            responsivePadding
            className="mb-4"
            headerActions={
                activeFilterCount > 0 ? (
                    <Button size="sm" variant="outline-dark" onClick={onClear}>
                        Clear filters ({activeFilterCount})
                    </Button>
                ) : null
            }
            title="Filters"
        >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                <FormSearchInput
                    noMargin
                    label="Search"
                    placeholder="Name, ID, family or configuration"
                    value={filters.search}
                    onChange={(event) => onChange({ search: event.target.value })}
                />
                <SearchSelect
                    noMargin
                    items={familyItems}
                    label="Families"
                    mode="multi"
                    placeholder="All families"
                    value={filters.familyIds}
                    onChange={(familyIds) => onChange({ familyIds })}
                />
                <FormSelect
                    noMargin
                    label="Timeframe"
                    options={[
                        { label: 'All timeframes', value: '' },
                        ...durations.map((duration) => ({ label: duration, value: duration }))
                    ]}
                    value={filters.duration ?? ''}
                    onChange={(event) => onChange({ duration: durations.find((duration) => duration === event.target.value) ?? null })}
                />
                <FormSelect
                    noMargin
                    helpText="Which configuration represents each strategy"
                    label="Best configuration by"
                    options={LEADERBOARD_RANK_BY_OPTIONS}
                    value={filters.rankBy}
                    onChange={(event) =>
                        onChange({
                            rankBy:
                                LEADERBOARD_RANK_BY_OPTIONS.find((option) => option.value === event.target.value)?.value ??
                                DEFAULT_LEADERBOARD_FILTERS.rankBy
                        })
                    }
                />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-zinc-400">Status</span>
                    <SegmentedControl
                        ariaLabel="Status"
                        options={STATUS_OPTIONS}
                        value={filters.status}
                        onChange={(status) => onChange({ status })}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-zinc-400">Role</span>
                    <SegmentedControl
                        ariaLabel="Role"
                        options={ROLE_OPTIONS}
                        value={filters.role}
                        onChange={(role) => onChange({ role })}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-zinc-400">Direction</span>
                    <SegmentedControl
                        ariaLabel="Direction"
                        options={BIAS_OPTIONS}
                        value={filters.bias}
                        onChange={(bias) => onChange({ bias })}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-zinc-400">Portfolios</span>
                    <SegmentedControl
                        ariaLabel="Portfolio subscriptions"
                        options={SUBSCRIPTION_OPTIONS}
                        value={filters.subscription}
                        onChange={(subscription) => onChange({ subscription })}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-zinc-400">Instruments</span>
                    <InstrumentFilter selected={filters.instruments} onChange={(instruments) => onChange({ instruments })} />
                </div>
                <FormCheckbox
                    checked={filters.resultsOnly}
                    label="Only strategies with results"
                    labelClassName="font-normal text-zinc-300"
                    onChange={(event) => onChange({ resultsOnly: event.target.checked })}
                />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {LEADERBOARD_THRESHOLDS.map((threshold) => (
                    <FormNumberInput
                        noMargin
                        allowNegative={!threshold.integer}
                        integer={threshold.integer}
                        key={threshold.key}
                        label={threshold.label}
                        min={threshold.integer ? 0 : undefined}
                        placeholder="Any"
                        suffix={threshold.suffix}
                        value={filters.thresholds[threshold.key] ?? null}
                        onValueChange={(value) => onChange({ thresholds: withThreshold(filters.thresholds, threshold.key, value) })}
                    />
                ))}
            </div>
        </Card>
    );
};

const StrategyLeaderboard: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = useMemo(() => parseLeaderboardFilters(searchParams, STANDARD_DURATIONS), [searchParams]);
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(loadColumnVisibility);

    useEffect(() => {
        saveColumnVisibility(columnVisibility);
    }, [columnVisibility]);

    const leaderboardQuery = useAppQuery(
        {
            ...trpc.strategyVariantComparison.listLeaderboard.queryOptions({ rankBy: filters.rankBy }),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME,
            placeholderData: keepPreviousData
        },
        // The page renders the error in an alert, so a toast would only repeat it.
        { fallbackErrorMessage: 'Failed to load the strategy leaderboard', showErrorToast: false }
    );

    const entries = useMemo(() => leaderboardQuery.data?.entries ?? [], [leaderboardQuery.data]);

    const updateFilters = useCallback(
        (patch: Partial<LeaderboardFilters>) => setSearchParams(serializeLeaderboardFilters({ ...filters, ...patch }), { replace: true }),
        [filters, setSearchParams]
    );

    const clearFilters = useCallback(
        () => setSearchParams(serializeLeaderboardFilters({ ...DEFAULT_LEADERBOARD_FILTERS, rankBy: filters.rankBy }), { replace: true }),
        [filters.rankBy, setSearchParams]
    );

    const { familyItems, durations, nameById } = useMemo(() => {
        const familyNameById = new Map<number, string>();
        const durationSet = new Set<BarDuration>();
        const names = new Map<number, string>();

        for (const entry of entries) {
            names.set(entry.strategyId, entry.name);
            if (entry.duration !== null) durationSet.add(entry.duration);

            if (entry.familyId !== null && entry.familyName !== null) {
                familyNameById.set(entry.familyId, entry.familyName);
            }
        }

        return {
            familyItems: [...familyNameById.entries()]
                .map(([id, name]): SearchSelectItem => ({ value: id, label: `${name} (#${id})`, name }))
                .sort((left, right) => left.name.localeCompare(right.name)),
            durations: STANDARD_DURATIONS.filter((duration) => durationSet.has(duration)),
            nameById: names
        };
    }, [entries]);

    const visibleEntries = useMemo(() => sortByRank(filterLeaderboardEntries(entries, filters), filters.rankBy), [entries, filters]);

    const columns = useMemo(() => buildColumns(nameById), [nameById]);

    const resultCount = useMemo(() => entries.filter((entry) => entry.bestCombination !== null).length, [entries]);
    const rerunCount = useMemo(() => entries.filter((entry) => entry.requiresBacktestRefresh).length, [entries]);
    const unresolvedCount = useMemo(() => entries.filter((entry) => entry.versionError !== null).length, [entries]);

    if (leaderboardQuery.isLoading) {
        return (
            <UserLayout title="Strategy Leaderboard - Project 314">
                <DataLoadingSpinner text="Loading strategy results..." />
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="2200px" title="Strategy Leaderboard - Project 314">
            <div className="container mx-auto px-4">
                <div className="mb-4 mt-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                        <h3 className="text-xl font-semibold text-white">Strategy Leaderboard</h3>
                        <p className="mt-1 max-w-3xl text-sm text-zinc-400">
                            Every strategy and variation, each shown with its best option-strategy and exit combination for its current
                            version. Walk-forward (WF) columns cover only trades after the training cutoff, so they are the better guard
                            against overfitting.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" to="/user/tradingStrategies" variant="outline-dark">
                            Trading Strategies
                        </Button>
                        <Button
                            disabled={leaderboardQuery.isFetching}
                            icon={<FaSyncAlt />}
                            size="sm"
                            variant="outline-dark"
                            onClick={() => void leaderboardQuery.refetch()}
                        >
                            {leaderboardQuery.isFetching ? 'Refreshing…' : 'Refresh'}
                        </Button>
                    </div>
                </div>

                {leaderboardQuery.error && (
                    <Alert className="mb-4" variant="danger">
                        {leaderboardQuery.error.message}
                    </Alert>
                )}

                <FiltersCard
                    durations={durations}
                    familyItems={familyItems}
                    filters={filters}
                    onChange={updateFilters}
                    onClear={clearFilters}
                />

                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm text-zinc-400">
                        Showing <span className="font-medium text-zinc-200">{visibleEntries.length}</span> of {entries.length} strategies ·{' '}
                        {resultCount} with results
                        {rerunCount > 0 && <span className="text-amber-400"> · {rerunCount} need a re-run</span>}
                        {unresolvedCount > 0 && <span className="text-red-400"> · {unresolvedCount} can&apos;t be evaluated</span>}
                    </div>
                    <ColumnVisibilityMenu
                        columns={COLUMN_OPTIONS}
                        defaultVisibility={DEFAULT_COLUMN_VISIBILITY}
                        visibility={columnVisibility}
                        onChange={setColumnVisibility}
                    />
                </div>

                {visibleEntries.length === 0 ? (
                    <EmptyState
                        action={
                            countActiveFilters(filters) > 0 ? (
                                <Button size="sm" variant="outline-dark" onClick={clearFilters}>
                                    Clear filters
                                </Button>
                            ) : undefined
                        }
                        description={
                            entries.length === 0
                                ? 'Create a trading strategy to see it here.'
                                : 'No strategy matches every filter. Loosen a threshold or clear the filters.'
                        }
                        title={entries.length === 0 ? 'No strategies yet' : 'No matching strategies'}
                    />
                ) : (
                    <DataTable
                        striped
                        columnVisibility={columnVisibility}
                        columns={columns}
                        data={visibleEntries}
                        getRowId={(entry) => String(entry.strategyId)}
                        maxHeight="75vh"
                        pagination={{ enabled: true, pageSize: 50 }}
                        search={false}
                        onColumnVisibilityChange={setColumnVisibility}
                    />
                )}
            </div>
        </UserLayout>
    );
};

export default StrategyLeaderboard;
